import { motion } from 'framer-motion'
import { useParams, Link, Navigate } from 'react-router-dom'
import { Calendar, Clock, ArrowLeft, Share2 } from 'lucide-react'
import { useLanguage } from '../context/LanguageContext'
import { blogPosts } from '../data/blogPosts'
import SEO from '../components/SEO'

const BlogPost = () => {
  const { slug } = useParams()
  const { language } = useLanguage()
  
  const post = blogPosts.find(p => p.slug === slug)
  
  if (!post) {
    return <Navigate to="/blog" replace />
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString(language === 'sq' ? 'sq-AL' : 'en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    })
  }

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: post.title[language],
        text: post.excerpt[language],
        url: window.location.href,
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
      alert(language === 'sq' ? 'Link-u u kopjua!' : 'Link copied!')
    }
  }

  return (
    <>
      <SEO 
        title={post.title[language]}
        description={post.excerpt[language]}
        keywords={`${post.category}, blog, contenta marketing, ${language === 'sq' ? 'shqipëri' : 'albania'}`}
      />
      
      <div className="mt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-dark via-bronze-950 to-dark relative overflow-hidden">
          <motion.div
            animate={{ x: [0, 100, 0], y: [0, 50, 0], scale: [1, 1.2, 1], opacity: [0.03, 0.08, 0.03] }}
            transition={{ duration: 25, repeat: Infinity }}
            className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-bronze rounded-full blur-3xl"
          />
          
          <div className="container mx-auto px-4 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
            >
              {/* Back Button */}
              <Link 
                to="/blog"
                className="inline-flex items-center gap-2 text-gold-400 hover:text-gold-300 mb-8 transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
                <span>{language === 'sq' ? 'Kthehu te Blog' : 'Back to Blog'}</span>
              </Link>

              {/* Category Badge */}
              <div className="mb-4">
                <span className="px-4 py-1.5 bg-gold-400 text-dark text-sm font-semibold rounded-full">
                  {post.category}
                </span>
              </div>

              {/* Title */}
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-cream mb-6 max-w-4xl">
                {post.title[language]}
              </h1>

              {/* Meta Info */}
              <div className="flex flex-wrap items-center gap-6 text-beige-300">
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(post.date)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>5 {language === 'sq' ? 'min lexim' : 'min read'}</span>
                </div>
                <button
                  onClick={handleShare}
                  className="flex items-center gap-2 text-gold-400 hover:text-gold-300 transition-colors"
                >
                  <Share2 className="w-5 h-5" />
                  <span>{language === 'sq' ? 'Shpërndaj' : 'Share'}</span>
                </button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="relative -mt-8">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="rounded-2xl overflow-hidden shadow-2xl border border-gold-400/20"
            >
              <img 
                src={post.image} 
                alt={post.title[language]}
                className="w-full h-[400px] object-cover"
              />
            </motion.div>
          </div>
        </section>

        {/* Content */}
        <section className="py-16 bg-gradient-to-b from-bronze-950 to-dark relative overflow-hidden">
          <motion.div
            animate={{
              x: [0, -50, 0],
              y: [0, 50, 0],
              scale: [1, 1.2, 1],
              opacity: [0.05, 0.12, 0.05]
            }}
            transition={{ duration: 20, repeat: Infinity }}
            className="absolute top-20 right-20 w-96 h-96 bg-gradient-gold rounded-full blur-3xl"
          />
          
          <div className="container mx-auto px-4 relative z-10">
            <div className="max-w-4xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="prose prose-lg prose-invert prose-gold max-w-none"
                dangerouslySetInnerHTML={{ __html: post.content[language] }}
                style={{
                  color: '#C8C1B0',
                }}
              />

              {/* CTA Section */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
                className="mt-16 p-8 bg-gradient-to-br from-gold-400/10 to-bronze-400/10 rounded-2xl border border-gold-400/20"
              >
                <h3 className="text-2xl font-bold text-cream mb-4">
                  {language === 'sq' 
                    ? 'Gati për të filluar projektin tuaj?' 
                    : 'Ready to start your project?'}
                </h3>
                <p className="text-beige-200 mb-6">
                  {language === 'sq'
                    ? 'Kontaktoni Contenta Marketing sot për një konsultim falas dhe zbuloni si mund t\'ju ndihmojmë të arrini qëllimet tuaja.'
                    : 'Contact Contenta Marketing today for a free consultation and discover how we can help you achieve your goals.'}
                </p>
                <Link
                  to="/contact"
                  className="inline-block px-8 py-3 bg-gradient-gold text-dark font-semibold rounded-full hover:shadow-gold transition-all duration-300"
                >
                  {language === 'sq' ? 'Kontaktoni Tani' : 'Contact Now'}
                </Link>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        <section className="py-16 bg-dark">
          <div className="container mx-auto px-4">
            <h2 className="text-3xl font-bold text-cream mb-8 text-center">
              {language === 'sq' ? 'Artikuj të Tjerë' : 'Other Articles'}
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-4xl mx-auto">
              {blogPosts
                .filter(p => p.id !== post.id)
                .slice(0, 2)
                .map((relatedPost, index) => (
                  <motion.div
                    key={relatedPost.id}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <Link to={`/blog/${relatedPost.slug}`}>
                      <div className="bg-bronze-900/30 backdrop-blur-sm rounded-2xl overflow-hidden border border-gold-400/20 hover:border-gold-400/40 transition-all duration-500 hover:transform hover:scale-105">
                        <img 
                          src={relatedPost.image} 
                          alt={relatedPost.title[language]}
                          className="w-full h-48 object-cover"
                        />
                        <div className="p-6">
                          <span className="text-gold-400 text-sm font-semibold">
                            {relatedPost.category}
                          </span>
                          <h3 className="text-xl font-bold text-cream mt-2 mb-3">
                            {relatedPost.title[language]}
                          </h3>
                          <p className="text-beige-200 text-sm line-clamp-2">
                            {relatedPost.excerpt[language]}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </motion.div>
                ))}
            </div>
          </div>
        </section>
      </div>
    </>
  )
}

export default BlogPost
